import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Lil'Clucker light-weight interactive elements.
 *
 * <lc-swatch-gallery>  — colour swatches that swap a product image (heated base feature).
 * <lc-plan-picker>     — subscribe / one-time radio cards that update the CTA label.
 * <lc-cart-recs>       — "Complete your setup" add buttons inside the cart drawer.
 *
 * All are progressive enhancements: the server-rendered markup is usable without JS.
 */

/**
 * <lc-cart-recs> — cart-drawer recommendations. Each [data-lc-rec-add] button adds one of the
 * variant in data-variant-id through Shopify's cart API and hands the response's section HTML to
 * Horizon via CartLinesUpdateEvent, so the drawer re-renders itself the same way as any other add.
 */
class LcCartRecs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lc-rec-add]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;
    const id = Number(button.dataset.variantId);
    if (!id) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.lcAdding || 'Adding…';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const sections = this.dataset.sectionId || 'cart-drawer-section';
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections }),
      });
      if (!response.ok) throw new Error('The cart is temporarily unavailable. Please try again.');
      const json = await response.json();
      if (json.status) {
        this.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
        throw new Error(json.description || json.message || 'Add to cart failed');
      }
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lc-cart-recs', sourceId: this.id, itemCount: 1, sections: json.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      button.textContent = label;
      console.warn('[lc-cart-recs]', error);
    }
  };
}

/**
 * "Added to your cart" notice: flag the cart drawer after a successful add so the drawer's
 * notice (rendered in snippets/cart-drawer.liquid) shows until the drawer closes or the cart
 * changes again. The attribute lives on <theme-drawer>, which Horizon never re-renders.
 */
const lcCartDrawer = () => document.getElementById('cart-drawer');

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const drawer = lcCartDrawer();
  if (!drawer) return;
  const action = /** @type {{ action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} */ (event).action;
  if (action === 'add') {
    /** @type {any} */ (event).promise
      ?.then(({ detail }) => {
        if (!detail?.didError) drawer.setAttribute('data-lc-just-added', '');
      })
      .catch(() => {});
  } else {
    drawer.removeAttribute('data-lc-just-added');
  }
});

document.addEventListener('theme-drawer:close', (event) => {
  if (event.target instanceof Element && event.target.id === 'cart-drawer') event.target.removeAttribute('data-lc-just-added');
});

class LcSwatchGallery extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const initial = this.querySelector('[data-lc-swatch][aria-pressed="true"]');
    if (initial instanceof HTMLElement) this.#select(initial);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-lc-swatch]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    this.#select(target);
  };

  /** @param {HTMLElement} swatch */
  #select(swatch) {
    const value = swatch.dataset.lcSwatch;
    if (!value) return;

    for (const other of this.querySelectorAll('[data-lc-swatch]')) {
      other.setAttribute('aria-pressed', other === swatch ? 'true' : 'false');
    }

    for (const image of this.querySelectorAll('[data-lc-swatch-image]')) {
      if (!(image instanceof HTMLElement)) continue;
      image.hidden = image.dataset.lcSwatchImage !== value;
    }

    const caption = this.querySelector('[data-lc-swatch-caption]');
    if (caption instanceof HTMLElement) {
      const template = caption.dataset.lcSwatchCaption || '[colour]';
      caption.textContent = template.replace('[colour]', value);
    }
  }
}

class LcPlanPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = () => this.#sync();

  #sync() {
    const checked = this.querySelector('input[type="radio"]:checked');
    for (const card of this.querySelectorAll('[data-lc-plan]')) {
      const input = card.querySelector('input[type="radio"]');
      card.setAttribute('aria-checked', input && input === checked ? 'true' : 'false');
    }

    if (!(checked instanceof HTMLInputElement)) return;

    const label = checked.dataset.lcCtaLabel;
    const target = this.closest('[data-lc-plan-scope]') ?? this.parentElement;
    const cta = target?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (cta instanceof HTMLButtonElement && cta.disabled) return;
    if (cta instanceof HTMLElement && label) {
      const textNode = Array.from(cta.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
      if (textNode) {
        textNode.textContent = label;
      } else {
        cta.prepend(document.createTextNode(label));
      }
    }
  }
}

/**
 * <lc-tabs> — segmented control: buttons carry data-lc-tab="key", panels carry
 * data-lc-panel="key". The active button gets aria-pressed="true"; other panels are hidden.
 */
class LcTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const initial = this.querySelector('[data-lc-tab][aria-pressed="true"]') ?? this.querySelector('[data-lc-tab]');
    if (initial instanceof HTMLElement) this.#select(initial.dataset.lcTab ?? '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-lc-tab]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    this.#select(target.dataset.lcTab ?? '');
  };

  /** @param {string} key */
  #select(key) {
    for (const button of this.querySelectorAll('[data-lc-tab]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-pressed', button.dataset.lcTab === key ? 'true' : 'false');
    }
    for (const panel of this.querySelectorAll('[data-lc-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.lcPanel !== key;
    }
  }
}

/**
 * <lc-disclosure> — a button with data-lc-toggle shows/hides the element with data-lc-target
 * and swaps its own label between data-lc-show / data-lc-hide.
 */
class LcDisclosure extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lc-toggle]') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    const target = this.querySelector('[data-lc-target]');
    if (!(target instanceof HTMLElement)) return;
    target.hidden = !target.hidden;
    button.setAttribute('aria-expanded', String(!target.hidden));
    const label = target.hidden ? button.dataset.lcShow : button.dataset.lcHide;
    if (label) button.textContent = label;
  };
}

/**
 * <lc-colour-swap> — holds one image per colourway (children with data-lc-colour="Name", plus a
 * default with data-lc-colour=""). The PDP buy box records the selected colour on
 * <html data-lc-colour> and dispatches `lc:colour`; the matching slot is shown, else the default.
 * Lets feature, how-it-works and compatibility imagery follow the colour the shopper is looking at.
 */
class LcColourSwap extends HTMLElement {
  connectedCallback() {
    document.addEventListener('lc:colour', this.#onColour);
    this.#apply(document.documentElement.dataset.lcColour || '');
  }

  disconnectedCallback() {
    document.removeEventListener('lc:colour', this.#onColour);
  }

  /** @param {Event} event */
  #onColour = (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    this.#apply(String(detail?.colour ?? ''));
  };

  /** @param {string} colour */
  #apply(colour) {
    const slots = Array.from(this.querySelectorAll(':scope > [data-lc-colour]'));
    if (!slots.length) return;
    const wanted = colour.trim().toLowerCase();
    const match = wanted ? slots.find((slot) => slot instanceof HTMLElement && slot.dataset.lcColour?.trim().toLowerCase() === wanted) : null;
    const fallback = slots.find((slot) => slot instanceof HTMLElement && slot.dataset.lcColour === '') ?? slots[0];
    const active = match ?? fallback;
    for (const slot of slots) {
      if (slot instanceof HTMLElement) slot.hidden = slot !== active;
    }
  }
}

/**
 * <lc-article-toc> — builds an "In this guide" list from the h2 headings of the element named by
 * data-lc-toc-source, and highlights the heading currently in view.
 */
class LcArticleToc extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const source = document.getElementById(this.dataset.lcTocSource || '');
    const list = this.querySelector('[data-lc-toc-links]');
    if (!source || !list) return;
    // Prefer h2 sections; older articles are structured with h3 (or bold "headings") only.
    let headings = Array.from(source.querySelectorAll('h2'));
    if (!headings.length) headings = Array.from(source.querySelectorAll('h3'));
    if (headings.length < 2) return;

    headings.forEach((h, i) => {
      if (!h.id) h.id = `lc-h-${i + 1}`;
      const link = document.createElement('a');
      link.className = 'lc-art__toc-link';
      link.href = `#${h.id}`;
      link.textContent = h.textContent?.trim() || `Section ${i + 1}`;
      list.appendChild(link);
    });
    this.hidden = false;

    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        for (const link of list.querySelectorAll('a')) {
          link.classList.toggle('lc-art__toc-link--on', link.getAttribute('href') === `#${visible.target.id}`);
        }
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );
    for (const h of headings) this.#observer.observe(h);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

/** <lc-read-progress> — thin reading-progress bar (mobile article header). */
class LcReadProgress extends HTMLElement {
  connectedCallback() {
    this.#update();
    window.addEventListener('scroll', this.#update, { passive: true });
    window.addEventListener('resize', this.#update);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#update);
    window.removeEventListener('resize', this.#update);
  }

  #update = () => {
    const bar = this.querySelector('[data-lc-progress]');
    if (!(bar instanceof HTMLElement)) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const pct = max > 0 ? Math.min(100, Math.max(0, (window.scrollY / max) * 100)) : 0;
    bar.style.width = `${pct}%`;
  };
}

if (!customElements.get('lc-cart-recs')) customElements.define('lc-cart-recs', LcCartRecs);
if (!customElements.get('lc-article-toc')) customElements.define('lc-article-toc', LcArticleToc);
if (!customElements.get('lc-read-progress')) customElements.define('lc-read-progress', LcReadProgress);
if (!customElements.get('lc-tabs')) customElements.define('lc-tabs', LcTabs);
if (!customElements.get('lc-colour-swap')) customElements.define('lc-colour-swap', LcColourSwap);
if (!customElements.get('lc-disclosure')) customElements.define('lc-disclosure', LcDisclosure);
if (!customElements.get('lc-swatch-gallery')) customElements.define('lc-swatch-gallery', LcSwatchGallery);
if (!customElements.get('lc-plan-picker')) customElements.define('lc-plan-picker', LcPlanPicker);
